import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type Ref, useEffect, useState } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

const trackVariants = cva("w-full h-1.5", {
	variants: {
		startFull: {
			true: "bg-surface-muted",
			false: "bg-surface-dark",
		},
	},
	defaultVariants: {
		startFull: false,
	},
});

const fillVariants = cva("h-1.5", {
	variants: {
		startFull: {
			true: "bg-surface-dark",
			false: "bg-surface-muted",
		},
	},
	defaultVariants: {
		startFull: false,
	},
});

export type TimeProgressBarProps = HTMLAttributes<HTMLDivElement> &
	VariantProps<typeof trackVariants> & {
		/** Total run time of the bar, in milliseconds. */
		totalDurationMs: number;
		trackingId?: string;
		ref?: Ref<HTMLDivElement>;
	};

export const TimeProgressBar: FC<TimeProgressBarProps> = ({
	totalDurationMs,
	startFull,
	trackingId,
	className,
	ref,
	...rest
}) => {
	const [progressPct, setProgressPct] = useState(0);

	useEffect(() => {
		const startTime = Date.now();

		const interval = setInterval(() => {
			const elapsed = Date.now() - startTime;
			const pct = Math.min((elapsed / totalDurationMs) * 100, 100);
			setProgressPct(pct);
			if (pct === 100) {
				clearInterval(interval);
			}
		}, 50);

		return () => clearInterval(interval);
	}, [totalDurationMs]);

	return (
		<div
			ref={ref}
			data-testid="time-progress-bar"
			className={cn(trackVariants({ startFull }), className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			<div className={fillVariants({ startFull })} style={{ width: `${progressPct}%` }} />
		</div>
	);
};
TimeProgressBar.displayName = "TimeProgressBar";
